import { MediaItem } from '../types';

interface ContainerDimensions {
  width: string;
  height: string;
  maxWidth: string;
  maxHeight: string;
}

interface ViewportConstraints {
  maxWidth: number;
  maxHeight: number;
  padding: number;
}

/**
 * Calculate optimal container dimensions based on media metadata
 * while respecting viewport constraints
 */
export const calculateOptimalDimensions = (
  media: MediaItem,
  viewportConstraints: ViewportConstraints
): ContainerDimensions => {
  if (!media.metadata) {
    // Fallback to default dimensions if no metadata
    return {
      width: '100%',
      height: '60vh',
      maxWidth: '80vw',
      maxHeight: '80vh'
    };
  }

  const { width: mediaWidth, height: mediaHeight } = media.metadata;
  const { maxWidth: vpMaxWidth, maxHeight: vpMaxHeight, padding } = viewportConstraints;

  // Calculate available space (viewport minus padding)
  const availableWidth = vpMaxWidth - (padding * 2);
  const availableHeight = vpMaxHeight - (padding * 2);

  // Calculate aspect ratio
  const aspectRatio = mediaWidth / mediaHeight;

  // Calculate dimensions that fit within available space while maintaining aspect ratio
  let optimalWidth = availableWidth;
  let optimalHeight = availableWidth / aspectRatio;

  // If height exceeds available space, scale down based on height
  if (optimalHeight > availableHeight) {
    optimalHeight = availableHeight;
    optimalWidth = availableHeight * aspectRatio;
  }

  // Ensure minimum dimensions for very small media
  const minDimension = 200;
  if (optimalWidth < minDimension) {
    optimalWidth = minDimension;
    optimalHeight = minDimension / aspectRatio;
  }
  if (optimalHeight < minDimension) {
    optimalHeight = minDimension;
    optimalWidth = minDimension * aspectRatio;
  }

  return {
    width: `${Math.round(optimalWidth)}px`,
    height: `${Math.round(optimalHeight)}px`,
    maxWidth: `${availableWidth}px`,
    maxHeight: `${availableHeight}px`
  };
};

/**
 * Get viewport constraints based on screen size
 */
export const getViewportConstraints = (): ViewportConstraints => {
  const isMobile = window.innerWidth <= 768;
  const isFullscreen = !!document.fullscreenElement;
  
  // In fullscreen mode, use the full screen dimensions
  // Otherwise, account for header and padding
  if (isFullscreen) {
    return {
      maxWidth: window.innerWidth,
      maxHeight: window.innerHeight,
      padding: isMobile ? 20 : 40
    };
  }
  
  return {
    maxWidth: window.innerWidth,
    maxHeight: window.innerHeight - (isMobile ? 120 : 100), // Account for header
    padding: isMobile ? 20 : 40
  };
};

/**
 * Determine if media is portrait, landscape, or square
 */
export const getMediaOrientation = (media: MediaItem): 'portrait' | 'landscape' | 'square' => {
  if (!media.metadata) return 'landscape';
  
  const { width, height } = media.metadata;
  const aspectRatio = width / height;
  
  if (aspectRatio > 1.1) return 'landscape';
  if (aspectRatio < 0.9) return 'portrait';
  return 'square';
};
